// client/src/reservations/utils/fees.js
import { DateTime } from 'luxon';

function round2(n) { return Math.round((Number(n)||0) * 100) / 100; }

function clampToBase(v, base) {
  if (!Number.isFinite(v)) return 0;
  return Math.max(0, Math.min(v, base));
}

function amountFromType(base, amt, type, fullRequired) {
  if (fullRequired) return base;
  if (amt == null) return 0;
  const t = String(type || '').toLowerCase();
  if (t === 'percentage' || t === 'percent') return base * (Number(amt) / 100);
  if (t === 'flat' || t === 'fixed') return Number(amt);
  // default to flat if unknown
  return Number(amt);
}

function getLocalStartISO(it, tz, checkIn='15:00:00') {
  try {
    if (it.offering_kind === 'ac_room_type') {
      const s = it?.guests_json?.ac_start_date;
      if (!s) return null;
      const t = checkIn || '15:00:00';
      return `${s}T${t}`;
    }
    return it?.guests_json?.slot_start_local || it?.slot_start || null;
  } catch { return null; }
}

export function computeQuote({ items = [], feesCtx, timezone = 'UTC', checkInAfter = '15:00:00' }) {
  const now = DateTime.now().setZone(timezone);

  const policiesById = {};
  (feesCtx?.policies || []).forEach(p => { policiesById[p.id] = p; });
  const map = feesCtx?.map || {};
  const allPolicyId = feesCtx?.all_policy_id || null;
  const modeForKind = {
    ps_service:    'personal_services',
    cb_class:      'class_based',
    ms_table_type: 'modular_seating',
    ac_room_type:  'accommodation',
  };

  let subtotal = 0;
  let payNow = 0;
  let payLater = 0;
  let earliestLaterAt = null;
  const laterBuckets = new Map();
  let hold = 0;

  for (const it of (items || [])) {
    const base = Number(it.base_price_preview || 0);
    subtotal += base;

    const mode = modeForKind[it.offering_kind] || it.offering_kind;
    // Modular seating is keyed by seating_config_id in rc_fee_policy_applies
    const idForKey = it.offering_kind === 'ms_table_type'
      ? (it?.guests_json?.ms_seating_config_id || it.ms_seating_config_id || it.offering_id)
      : it.offering_id;
    const key = `${mode}|${idForKey}`;
    const pid = map[key] || allPolicyId;
    const policy = pid ? policiesById[pid] : null;
    if (!policy) continue;

    // Hold (no-show preauth) — taken at booking time
    if (policy.no_show_fee_amount != null) {
      const holdAmt = amountFromType(base, policy.no_show_fee_amount, policy.no_show_fee_type, false);
      hold += clampToBase(holdAmt, base);
    }

    // Prepayment — when to charge?
    const preAmt = amountFromType(base, policy.prepayment_amount, policy.prepayment_type, policy.full_prepayment_required);
    if (preAmt > 0) {
      const startISO = getLocalStartISO(it, timezone, checkInAfter);
      let chargeAt = now; // default: now
      if (startISO && Number(policy.charge_before_value) > 0) {
        const start = DateTime.fromISO(startISO, { zone: timezone });
        const unit = String(policy.charge_before_unit || 'hours').toLowerCase();
        const delta = unit === 'days'
          ? { days: Number(policy.charge_before_value) }
          : { hours: Number(policy.charge_before_value) };
        chargeAt = start.minus(delta);
      }
      // If the charge moment is in the past or now → pay now
      if (chargeAt <= now) {
        payNow += clampToBase(preAmt, base);
      } else {
        const amt = clampToBase(preAmt, base);
        payLater += amt;
        const iso = chargeAt.toISO(); // keep zone
        laterBuckets.set(iso, round2((laterBuckets.get(iso) || 0) + amt));
        if (!earliestLaterAt || chargeAt < earliestLaterAt) earliestLaterAt = chargeAt;
      }
    }
  }

  const payLaterSchedule = Array.from(laterBuckets.entries())
    .map(([at, amount]) => ({ at, amount: round2(amount) }))
    .sort((a, b) => (a.at < b.at ? -1 : a.at > b.at ? 1 : 0));

  return {
    subtotal: round2(subtotal),
    pay_now: round2(payNow),
    hold_amount: round2(hold),
    pay_later_total: round2(payLater),
    pay_later_at: earliestLaterAt ? earliestLaterAt.toISO() : null,
    pay_later_schedule: payLaterSchedule
  };
}
